<template>
  <div
    role="tablist"
    aria-label="Visão"
    class="inline-flex flex-wrap gap-1 rounded-lg bg-lightGray30 p-1"
    :data-testid="testId"
  >
    <button
      v-for="option in resolvedOptions"
      :key="option.value"
      type="button"
      role="tab"
      :aria-selected="option.value === modelValue"
      :data-testid="`${testId}-${option.value}`"
      class="rounded-md px-3.5 py-1.5 text-sm font-medium transition-colors"
      :class="
        option.value === modelValue
          ? 'bg-white text-textPrimary shadow-sm'
          : 'bg-transparent text-neutral50 hover:text-textPrimary'
      "
      @click="select(option.value)"
    >
      {{ option.label }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'

import { QUEUE_VIEW_OPTIONS } from './types'
import type { QueueViewMode, QueueViewOption } from './types'
import { readQueueViewPreference, writeQueueViewPreference } from './useQueueViewPreference'

const props = withDefaults(
  defineProps<{
    modelValue: QueueViewMode
    /** Chave da tela: a preferência é lembrada por tela, não global. */
    screenKey: string
    options?: QueueViewOption[]
    testId?: string
    /**
     * `false` quando quem monta já resolveu "URL > preferência" (ex.: `visao=` no link): o
     * switcher não reaplica a preferência no mount. Padrão `true` = comportamento de hoje.
     */
    restorePreference?: boolean
  }>(),
  { options: undefined, testId: 'queue-view-switcher', restorePreference: true }
)

const emit = defineEmits<{ 'update:modelValue': [value: QueueViewMode] }>()

const resolvedOptions = computed(() => props.options ?? QUEUE_VIEW_OPTIONS)

function select(value: QueueViewMode): void {
  writeQueueViewPreference(props.screenKey, value)
  emit('update:modelValue', value)
}

// A preferência é aplicada no mount, e não no `setup` do pai: quem abre a tela
// por um link com `?view=` já chega com a visão pedida, e só cai na preferência
// quando o pai não impôs nada diferente do padrão.
onMounted(() => {
  if (!props.restorePreference) return
  const preferred = readQueueViewPreference(props.screenKey, props.modelValue)
  if (preferred !== props.modelValue) emit('update:modelValue', preferred)
})
</script>
